import { PACK_TAKEDOWN_BULK_MAX_TARGETS, type PublishedPack } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { PackageIcon } from 'lucide-react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { usePacksCatalogContent } from '../../content/sections/packs-catalog.js';
import { fmtDate } from '../../lib/format.js';
import { visibilityLabel } from './pack-catalog-labels.js';

interface Props {
  readonly items: readonly PublishedPack[];
  /** Há busca ativa — diferencia "sem resultados" de "sem itens" (ADR-0702/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
  /** Navega para o detalhe do pack (/packs/$packId). */
  readonly onRowClick: (id: string) => void;
  /** Takedown é destrutivo → só super_admin vê a coluna de ações (ADR-0464). */
  readonly canTakedown: boolean;
  /** Pack cujo takedown está em flight — mostra loading só na linha certa. */
  readonly pendingPackId: string | null;
  readonly isTakingDown: boolean;
  readonly onTakedown: (pack: PublishedPack) => void;
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
  readonly onToggleAll: (checked: boolean) => void;
}

/* Tabela do catálogo de packs sobre o primitive `Table` de @planuze/ui (ADR-0700/0118).
   Coluna de contagem numérica com tabular-nums; ações de takedown por linha (super_admin).
   O nome do pack é um `<button>` (a11y por teclado) que navega ao detalhe — não `<tr>` onClick,
   que capturaria o botão de takedown (`noStaticElementInteractions`). O takedown fica em célula
   separada e ainda para a propagação por segurança (nunca navega). */
export function PacksCatalogTable({
  items,
  hasFilters,
  onClearFilters,
  onRowClick,
  canTakedown,
  pendingPackId,
  isTakingDown,
  onTakedown,
  selectedIds,
  onToggle,
  onToggleAll,
}: Props) {
  const { locale } = useAdminLocale();
  const copy = usePacksCatalogContent().table;
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<PackageIcon className="size-6" />}
        title={copy.emptyFilteredTitle}
        description={copy.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<PackageIcon className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
      />
    );
  }
  const selectableItems = items.filter((pack) => pack.latestActive !== null);
  const selectedVisibleCount = selectableItems.filter((pack) => selectedIds.has(pack.id)).length;
  const selectionTarget = Math.min(selectableItems.length, PACK_TAKEDOWN_BULK_MAX_TARGETS);
  const allSelected = selectionTarget > 0 && selectedVisibleCount >= selectionTarget;
  const selectionFull = selectedIds.size >= PACK_TAKEDOWN_BULK_MAX_TARGETS;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canTakedown && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={isTakingDown || selectableItems.length === 0}
                onCheckedChange={(checked) => onToggleAll(checked === true)}
                aria-label={copy.selectAllAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.columnPack}</TableHead>
          <TableHead>{copy.columnAuthor}</TableHead>
          <TableHead>{copy.columnTier}</TableHead>
          <TableHead>{copy.columnVisibility}</TableHead>
          <TableHead>{copy.columnActiveVersion}</TableHead>
          <TableHead>{copy.columnVersions}</TableHead>
          <TableHead>{copy.columnUpdated}</TableHead>
          {canTakedown && <TableHead>{copy.columnActions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((pack) => (
          <TableRow key={pack.id}>
            {canTakedown && (
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(pack.id)}
                  disabled={
                    isTakingDown ||
                    pack.latestActive === null ||
                    (selectionFull && !selectedIds.has(pack.id))
                  }
                  onCheckedChange={() => onToggle(pack.id)}
                  aria-label={
                    pack.latestActive === null
                      ? copy.selectRowAriaLabel(pack.displayName, copy.noActiveVersion)
                      : copy.selectRowAriaLabel(pack.displayName, pack.latestActive)
                  }
                />
              </TableCell>
            )}
            <TableCell>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="font-medium text-foreground transition-colors hover:underline"
                  onClick={() => onRowClick(pack.id)}
                >
                  {pack.displayName}
                </button>
                {pack.verified && (
                  <StatusBadge variant="success" dot>
                    {copy.verified}
                  </StatusBadge>
                )}
              </div>
              <div className="font-mono text-xs text-muted-foreground">{pack.id}</div>
            </TableCell>
            <TableCell className="text-muted-foreground">{pack.author}</TableCell>
            <TableCell className="text-muted-foreground">{pack.tier}</TableCell>
            <TableCell className="text-muted-foreground">
              {visibilityLabel(pack.visibility, locale)}
            </TableCell>
            <TableCell>
              {pack.latestActive === null ? (
                <StatusBadge variant="warning">{copy.noActiveVersion}</StatusBadge>
              ) : (
                <span className="font-mono text-xs text-muted-foreground">
                  v{pack.latestActive}
                </span>
              )}
            </TableCell>
            <TableCell numeric className="text-muted-foreground">
              {pack.versionCount}
            </TableCell>
            <TableCell className="text-muted-foreground">{fmtDate(pack.lastUpdated)}</TableCell>
            {canTakedown && (
              <TableCell>
                {pack.latestActive === null ? (
                  <span className="text-xs text-muted-foreground">—</span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={isTakingDown && pendingPackId === pack.id}
                    disabled={isTakingDown}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTakedown(pack);
                    }}
                  >
                    {copy.takedown}
                  </Button>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
